import React, {
  memo,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styled from "styled-components/macro";

interface StyleProps {
  left: number;
  top: number;
  isOpen: boolean;
  align: "top" | "right" | "left";
  type: "dark" | "light";
}

interface TooltipProps {
  title: string | ReactNode;
  showWithoutOverflow?: boolean;
  children?: ReactNode;
  align?: "top" | "right" | "left";
  styles?: React.CSSProperties;
  type?: "dark" | "light";
}

interface UseTooltip {
  top: number;
  left: number;
  shouldShow: boolean;
  onMouseEnter(): void;
  onMouseLeave(): void;
}

const TOOLTIP_WIDTH = 250;

const StyledTooltip = styled.div<StyleProps>`
  position: fixed;
  display: ${(props) => (props.isOpen ? "block" : "none")};
  background-color: ${(props) =>
    props.type === "dark" ? "var(--dark-blue)" : "var(--white)"};
  color: ${(props) => (props.type === "dark" ? "#fff" : "#000")};
  left: ${(props) => `${props.left}px`};
  top: ${(props) => `${props.top}px`};
  transform: ${(props) =>
    props.align === "top"
      ? "translate(-50%, calc(-100% - 0.5rem))"
      : props.align === "left"
      ? "translate(-100%, -50%)"
      : "translateY(-50%)"};
  z-index: 100000;
  padding: 0.5rem 0.75rem;
  width: max-content;
  max-width: ${TOOLTIP_WIDTH}px;
  border-radius: var(--rounded-corner);
  height: auto;
  word-wrap: break-word;
  white-space: normal;
  font-size: var(--font-size-small);
  box-shadow: ${(props) => (props.type === "dark" ? "" : "var(--drop-shadow)")};
  border: ${(props) =>
    props.type === "dark" ? "" : "1px solid var(--border-gray)"};
  transform-style: preserve-3d;

  &::before {
    content: "";
    position: absolute;
    bottom: ${(props) => (props.align === "top" ? "0" : "50%")};
    left: ${(props) =>
      props.align === "top" ? "50%" : props.align === "left" ? "100%" : "0"};
    width: 1rem;
    height: 1rem;
    background: ${(props) =>
      props.type === "dark" ? "var(--dark-blue)" : "var(--white)"};
    transform: translate(
        -50%,
        ${(props) => (props.align === "top" ? "10%" : "50%")}
      )
      translateZ(-1px) rotate(45deg);
    z-index: -1;
    box-shadow: ${(props) =>
      props.type === "dark" ? "" : "var(--drop-shadow)"};
    border: ${(props) =>
      props.type === "dark" ? "" : "1px solid var(--border-gray)"};
  }

  span {
    line-height: 1.5;
  }
`;

const useTooltip = (
  ref: React.RefObject<HTMLElement>,
  showWithoutOverflow: boolean,
  align: "top" | "right" | "left",
): UseTooltip => {
  const [isHover, setIsHover] = useState<boolean>(false);
  const [left, setLeft] = useState<number>(0);
  const [top, setTop] = useState<number>(0);
  const [shouldShow, setShouldShow] = useState<boolean>(false);

  useEffect(() => {
    const { current } = ref;

    if (!current) {
      return;
    }

    if (
      isHover &&
      (current.scrollWidth > current.clientWidth || showWithoutOverflow)
    ) {
      const { top, left, right, bottom } = current.getBoundingClientRect();

      if (align === "top") {
        setTop(top);
        setLeft((left + right) / 2);
      } else if (align === "right") {
        setTop(top + (bottom - top) / 2);
        // add buffer to the right
        setLeft(right + 25);
      } else if (align === "left") {
        setTop(top + (bottom - top) / 2);
        // add buffer to the left
        setLeft(left - 25);
      }
      setShouldShow(true);
    } else {
      setShouldShow(false);
    }
  }, [ref, isHover, showWithoutOverflow, align]);

  const onMouseEnter = useCallback(() => setIsHover(true), []);

  const onMouseLeave = useCallback(() => setIsHover(false), []);

  return { shouldShow, onMouseEnter, onMouseLeave, top, left };
};

const Tip = ({
  isOpen,
  left,
  top,
  title,
  align,
  type = "dark",
}: StyleProps & TooltipProps) => {
  const el = (
    <StyledTooltip
      type={type}
      align={align}
      isOpen={isOpen}
      top={top}
      left={left}
    >
      {typeof title === "string" ? <span>{title}</span> : title}
    </StyledTooltip>
  );

  return isOpen && String(title).length
    ? createPortal(el, document.body, "tooltip")
    : null;
};

/**
 * If you find that the tooltip is not working when wrapping a react component (i.e. not a native html element),
 * you might need to make that component a forwardRef AND
 * pass that component the rest of its props using the spread operator.
 *
 * It's easiest to do this if you define its prop types as:
 * interface Props extends React.HTMLAttributes<HTMLDivElement> {}
 * Then you can use the spread operator to pass the rest of the props to the component.
 */
export const Tooltip: React.FC<TooltipProps> = memo(
  ({
    title,
    children,
    showWithoutOverflow = false,
    align = "top",
    type = "dark",
  }) => {
    const ref = useRef<HTMLElement>(null);
    const { shouldShow, onMouseEnter, onMouseLeave, top, left } = useTooltip(
      ref,
      showWithoutOverflow,
      align,
    );

    const clone = React.cloneElement(children as ReactElement, {
      ref,
      onMouseEnter,
      onMouseLeave,
    });

    return (
      <>
        {clone}
        <Tip
          type={type}
          align={align}
          title={title}
          isOpen={shouldShow}
          top={top}
          left={left}
        />
      </>
    );
  },
);
